import React, { useState, useEffect } from 'react';
import {
  PlusIcon,
  TagIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

export default function Categorias() {
  const [categorias, setCategorias] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Estados do modal
  const [showModal, setShowModal] = useState(false);
  const [editingCategoria, setEditingCategoria] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    prefixo: '',
    descricao: '',
    cor: '#3B82F6',
    ativo: true
  });
  const [formErrors, setFormErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchCategorias();
  }, [search]);

  const fetchCategorias = async () => {
    try {
      const params = new URLSearchParams();
      if (search) params.append('search', search);

      const response = await api.get(`/categorias?${params}`);
      setCategorias(response.data);
    } catch (error) {
      console.error('Erro ao carregar categorias:', error);
      toast.error('Erro ao carregar categorias');
    } finally {
      setLoading(false);
    }
  };

  // Funções do modal
  const openModal = (categoria = null) => {
    if (categoria) {
      setEditingCategoria(categoria);
      setFormData({
        nome: categoria.nome,
        prefixo: categoria.prefixo || '',
        descricao: categoria.descricao || '',
        cor: categoria.cor,
        ativo: categoria.ativo
      });
    } else {
      setEditingCategoria(null);
      setFormData({
        nome: '',
        prefixo: '',
        descricao: '',
        cor: '#3B82F6',
        ativo: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCategoria(null);
    setFormData({
      nome: '',
      prefixo: '',
      descricao: '',
      cor: '#3B82F6',
      ativo: true
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    let processedValue = type === 'checkbox' ? checked : value;
    
    // Converter prefixo para maiúsculas e remover caracteres especiais
    if (name === 'prefixo') {
      processedValue = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: processedValue
    }));

    // Limpar erro do campo quando usuário começa a digitar
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.nome.trim()) {
      errors.nome = 'Nome é obrigatório';
    }

    if (formData.nome.trim().length < 2) {
      errors.nome = 'Nome deve ter pelo menos 2 caracteres';
    }

    if (formData.prefixo && formData.prefixo.trim().length > 6) {
      errors.prefixo = 'Prefixo deve ter no máximo 6 caracteres';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      if (editingCategoria) {
        // Atualizar categoria
        await api.put(`/categorias/${editingCategoria.id}`, formData);
        toast.success('Categoria atualizada com sucesso!');
      } else {
        // Criar nova categoria
        await api.post('/categorias', formData);
        toast.success('Categoria criada com sucesso!');
      }

      closeModal();
      fetchCategorias();
    } catch (error) {
      console.error('Erro ao salvar categoria:', error);
      const message = error.response?.data?.error || 'Erro ao salvar categoria';
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (categoria) => {
    if (!window.confirm(`Tem certeza que deseja excluir a categoria "${categoria.nome}"?`)) {
      return;
    }

    try {
      await api.delete(`/categorias/${categoria.id}`);
      toast.success('Categoria excluída com sucesso!');
      fetchCategorias();
    } catch (error) {
      console.error('Erro ao excluir categoria:', error);
      const message = error.response?.data?.error || 'Erro ao excluir categoria';
      toast.error(message);
    }
  };

  const getStatusText = (categoria) => {
    if (!categoria.ativo) {
      return { text: 'Inativa', class: 'bg-gray-100 text-gray-800' };
    }
    return { text: 'Ativa', class: 'bg-green-100 text-green-800' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Título */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Categorias</h1>
          <p className="mt-1 text-sm text-gray-500">
            Gerenciamento de categorias de itens
          </p>
        </div>
        <button
          onClick={() => openModal()}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nova Categoria
        </button>
      </div>

      {/* Busca */}
      <div className="card">
        <div className="card-body">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              className="input pl-10"
              placeholder="Buscar categorias..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>
      </div>

      {/* Lista de categorias */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {categorias.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <TagIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhuma categoria encontrada</h3>
            <p className="mt-1 text-sm text-gray-500">
              {search ? 'Tente ajustar a busca.' : 'Comece adicionando uma nova categoria.'}
            </p>
          </div>
        ) : (
          categorias.map((categoria) => {
            const status = getStatusText(categoria);
            return (
              <div key={categoria.id} className="card">
                <div className="card-body">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center">
                      <div
                        className="w-4 h-4 rounded-full mr-3"
                        style={{ backgroundColor: categoria.cor }}
                      />
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">
                          {categoria.nome}
                        </h3>
                        {categoria.prefixo && (
                          <span className="text-xs text-gray-500 font-mono bg-gray-100 px-2 py-0.5 rounded">
                            {categoria.prefixo}
                          </span>
                        )}
                      </div>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${status.class}`}>
                      {status.text}
                    </span>
                  </div>

                  {categoria.descricao && (
                    <p className="mt-2 text-sm text-gray-500">
                      {categoria.descricao}
                    </p>
                  )}

                  <div className="mt-4 flex items-center justify-between">
                    <span className="text-sm text-gray-500">
                      {categoria._count?.itens || 0} itens
                    </span>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => openModal(categoria)}
                        className="text-sm text-primary-600 hover:text-primary-900"
                      >
                        <PencilIcon className="h-4 w-4 inline mr-1" />
                        Editar
                      </button>
                      <button
                        onClick={() => handleDelete(categoria)}
                        className="text-sm text-red-600 hover:text-red-900"
                      >
                        <TrashIcon className="h-4 w-4 inline mr-1" />
                        Excluir
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Modal de Cadastro/Edição */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">
                {editingCategoria ? 'Editar Categoria' : 'Nova Categoria'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nome *
                </label>
                <input
                  type="text"
                  name="nome"
                  value={formData.nome}
                  onChange={handleInputChange}
                  className={`input ${formErrors.nome ? 'border-red-500' : ''}`}
                  placeholder="Nome da categoria"
                />
                {formErrors.nome && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.nome}</p>
                )}
              </div>

              {/* Prefixo */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Prefixo
                  <span className="text-xs text-gray-500 ml-2">
                    (Se não informado, será gerado automaticamente)
                  </span>
                </label>
                <input
                  type="text"
                  name="prefixo"
                  value={formData.prefixo}
                  onChange={handleInputChange}
                  className={`input font-mono ${formErrors.prefixo ? 'border-red-500' : ''}`}
                  placeholder="PROD"
                  maxLength={6}
                  style={{ textTransform: 'uppercase' }}
                />
                {formErrors.prefixo && (
                  <p className="text-red-500 text-xs mt-1">{formErrors.prefixo}</p>
                )}
                <p className="text-xs text-gray-500 mt-1">
                  Máximo de 6 caracteres. Usado para gerar códigos de itens.
                </p>
              </div>

              {/* Descrição */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Descrição
                </label>
                <textarea
                  name="descricao"
                  value={formData.descricao}
                  onChange={handleInputChange}
                  rows="3"
                  className="input"
                  placeholder="Descrição da categoria..."
                />
              </div>

              {/* Cor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cor
                </label>
                <div className="flex items-center space-x-3">
                  <input
                    type="color"
                    name="cor"
                    value={formData.cor}
                    onChange={handleInputChange}
                    className="w-12 h-10 rounded border border-gray-300 cursor-pointer"
                  />
                  <input
                    type="text"
                    name="cor"
                    value={formData.cor}
                    onChange={handleInputChange}
                    className="input flex-1"
                    placeholder="#3B82F6"
                  />
                </div>
              </div>

              {/* Ativo */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="ativo"
                  checked={formData.ativo}
                  onChange={handleInputChange}
                  className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                />
                <label className="ml-2 text-sm text-gray-700">
                  Categoria ativa
                </label>
              </div>

              {/* Botões */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="btn-secondary"
                  disabled={submitting}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={submitting}
                >
                  {submitting ? 'Salvando...' : (editingCategoria ? 'Atualizar' : 'Criar')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
} 